import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';

// Pages
import Auth from './pages/Auth';
import AdminLogin from './pages/AdminLogin';
import RequestsDirectory from './pages/RequestsDirectory';
import RequestDetail from './pages/RequestDetail';
import CreateRequest from './pages/CreateRequest';

// Dashboard
import Dashboard from './pages/dashboard/Dashboard';
import Profile from './pages/dashboard/Profile';
import MyDocuments from './pages/dashboard/MyDocuments';
import MyRequests from './pages/dashboard/MyRequests';
import MyArtisanProfile from './pages/dashboard/MyArtisanProfile';
import RespondRequests from './pages/dashboard/RespondRequests';
import MyApplications from './pages/dashboard/MyApplications';
import MyKYC from './pages/dashboard/MyKYC';
import MySubscription from './pages/dashboard/MySubscription';
import Notifications from './pages/dashboard/Notifications';
import AccessRequests from './pages/dashboard/AccessRequests';
import ProUpgrade from './pages/dashboard/ProUpgrade';
import Messaging from './pages/dashboard/Messaging';
import MessagingLegacyRedirect from './pages/dashboard/MessagingLegacyRedirect';
import MyProPage from './pages/dashboard/MyProPage';

// Route protégée pour utilisateurs
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/connexion" replace />;
  }

  return children;
};

// Route réservée au mode professionnel actif
const ProViewRoute = ({ children }) => {
  const { loading, isProView } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isProView()) {
    return <Navigate to="/tableau-de-bord/profil" replace />;
  }

  return children;
};

// Route protégée pour admins (connexion via /admin-login)
const AdminRoute = ({ children }) => {
  const { admin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin-login" replace />;
  }

  return children;
};

// Route publique (redirige si déjà connecté)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/tableau-de-bord" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Racine : plateforme app uniquement, pas de vitrine publique ici
          (site public → https://clicketpro.fr) */}
      <Route path="/" element={<Navigate to="/connexion" replace />} />
      <Route path="/deposer-demande" element={<CreateRequest />} />
      <Route path="/demandes" element={<RequestsDirectory />} />
      <Route path="/demandes/:id" element={<RequestDetail />} />

      {/* Auth Routes */}
      <Route path="/connexion" element={<PublicRoute><Auth /></PublicRoute>} />
      <Route path="/inscription" element={<Navigate to="/connexion" replace />} />
      
      {/* Admin Auth Route (séparé) */}
      <Route path="/admin-login" element={<AdminLogin />} />

      {/* Dashboard Routes */}
      <Route path="/tableau-de-bord" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/mon-profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/mes-documents" element={<ProtectedRoute><MyDocuments /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/mes-demandes" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/ma-fiche-artisan" element={<ProtectedRoute><ProViewRoute><MyArtisanProfile /></ProViewRoute></ProtectedRoute>} />
      <Route path="/tableau-de-bord/repondre-demandes" element={<ProtectedRoute><ProViewRoute><RespondRequests /></ProViewRoute></ProtectedRoute>} />
      <Route path="/tableau-de-bord/mes-offres-emploi" element={<Navigate to="/tableau-de-bord/mes-documents?tab=offres" replace />} />
      <Route path="/tableau-de-bord/mes-candidatures" element={<ProtectedRoute><MyApplications /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/kyc" element={<ProtectedRoute><ProViewRoute><MyKYC /></ProViewRoute></ProtectedRoute>} />
      <Route path="/tableau-de-bord/abonnement" element={<ProtectedRoute><MySubscription /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/favoris" element={<Navigate to="/tableau-de-bord/mes-documents?tab=favoris" replace />} />
      <Route path="/tableau-de-bord/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/demandes-acces" element={<ProtectedRoute><AccessRequests /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/passer-pro" element={<ProtectedRoute><ProUpgrade /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/post/:postId/chat/:conversationId" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/artisan/:artisanId/chat/:conversationId" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/chat/:conversationId" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/post/:postId" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/artisan/:artisanId" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie" element={<ProtectedRoute><Messaging /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/c/:conversationId" element={<ProtectedRoute><MessagingLegacyRedirect /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/messagerie/:conversationId" element={<ProtectedRoute><MessagingLegacyRedirect /></ProtectedRoute>} />
      <Route path="/tableau-de-bord/ma-page-pro" element={<ProtectedRoute><ProViewRoute><MyProPage /></ProViewRoute></ProtectedRoute>} />


      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
